import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { authStart } from '@ghostwright/api-client';

export default function SignIn() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await authStart(email);
      navigate('/auth/sent', { state: { email } });
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-border px-5 sm:px-8 py-4">
        <div className="max-w-container mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 text-heading">
            <span className="font-display text-lg">Ghostwright</span>
            <span className="text-primary leading-none" aria-hidden="true">●</span>
          </a>
        </div>
      </header>

      <main className="flex-1 px-5 sm:px-8 py-12 sm:py-24">
        <div className="max-w-[480px] mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="font-display text-[clamp(28px,4vw,36px)] font-normal leading-[1.15] tracking-[-0.018em] text-heading mb-3 [font-variation-settings:'opsz'_96,'SOFT'_60]">
              Sign in
            </h1>
            <p className="text-[15px] text-muted-foreground leading-relaxed">
              No password. We'll send you a link.
            </p>
          </div>

          {/* Card */}
          <form
            className="bg-card border border-border rounded-card p-8"
            autoComplete="on"
            onSubmit={handleSubmit}
          >
            <div className="flex flex-col gap-2 mb-5">
              <label
                htmlFor="email"
                className="text-sm font-medium text-foreground"
              >
                Email
              </label>
              <input
                type="email"
                id="email"
                className="w-full py-3.5 px-4 bg-background border border-border rounded-button text-base text-foreground min-h-[48px] transition-[border-color,box-shadow] duration-80 placeholder:text-placeholder focus:outline-none focus:border-primary focus:ring-[3px] focus:ring-primary/15"
                placeholder="you@email.com"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
              />
              <p className="text-xs text-muted-foreground">
                The email you'd like to use, or the one from your last order.
              </p>
            </div>

            {error && (
              <p className="text-sm text-destructive mb-4" role="alert">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground font-medium text-base py-3.5 px-6 rounded-button transition-opacity duration-80 hover:opacity-90 disabled:opacity-60 min-h-[48px]"
            >
              {submitting ? 'Sending...' : (
                <>
                  Send sign-in link
                  <span aria-hidden="true">&rarr;</span>
                </>
              )}
            </button>
          </form>
        </div>
      </main>

      <footer className="border-t border-border px-5 sm:px-8 py-6 text-xs text-muted-foreground">
        <div className="max-w-container mx-auto flex items-center justify-between flex-wrap gap-3">
          <span>
            Ghostwright<span className="text-primary mx-1.5" aria-hidden="true">●</span>2026
          </span>
        </div>
      </footer>
    </div>
  );
}
